import { compact } from 'lodash';

import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { useCanAccessPage } from '@/navigation/usePageAccessLookup';

export function useAuthorizedSettingsPages() {
  const currentUser = useCurrentUser();
  const { tenantSecurityConfiguration } = useTenantDetailsContext();
  const canAccessIntegrationsPage = useCanAccessPage(
    ROUTE_KEYS.ADMIN_INTEGRATIONS
  );
  const canAccessCategoriesConfigurationPage = useCanAccessPage(
    ROUTE_KEYS.ADMIN_CATEGORIES
  );
  const canAccessUserAdminPage = useCanAccessPage(ROUTE_KEYS.ADMIN_USERS);
  const canAccessTeamAdminPage = useCanAccessPage(ROUTE_KEYS.ADMIN_TEAMS);
  const canAccessBrandingPage = useCanAccessPage(ROUTE_KEYS.ADMIN_BRANDING);
  const canAccessSubBrandsPage = useCanAccessPage(ROUTE_KEYS.ADMIN_SUB_BRANDS);
  const canAccessLegalDisclaimersPage = useCanAccessPage(
    ROUTE_KEYS.ADMIN_LEGAL_DISCLAIMERS
  );
  const canAccessInternalToolsViaPermissions = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_INTERNAL_TOOLS
  );
  const canAccessAuditLogPage = useCanAccessPage(ROUTE_KEYS.ADMIN_AUDIT_LOG);
  const canAccessRolesPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_ROLES_ADMIN_PAGE
  );
  const canAccessSecurityPage = useCanAccessPage(ROUTE_KEYS.ADMIN_SECURITY);
  const canAccessSummaryPromptsPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_SUMMARY_PROMPTS_ADMIN_PAGE
  );
  const canAccessDocumentSummariesReportPage = useCanAccessPage(
    ROUTE_KEYS.ADMIN_DOCUMENT_SUMMARIES_REPORT
  );

  const canAccessPresentationTemplatesPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_TENANT_PRESENTATION_TEMPLATES_ADMIN_PAGE
  );

  const canAccessEducationPage = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_ACCESS_EDUCATION_ADMIN_PAGE
  );
  const canAccessInternalTools =
    canAccessInternalToolsViaPermissions || currentUser?.isInternalUser;
  const canRenderSecurityPage =
    canAccessSecurityPage &&
    (tenantSecurityConfiguration?.luminaryAccessControlEnabled ?? false);

  return compact([
    canAccessUserAdminPage && {
      display: 'Users',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_USERS, {}),
    },

    canAccessTeamAdminPage && {
      display: 'Teams',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_TEAMS, {}),
    },

    canAccessRolesPage && {
      display: 'Roles',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_ROLES, {}),
    },

    canAccessAuditLogPage && {
      display: 'Audit log',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_AUDIT_LOG, {}),
    },

    canAccessIntegrationsPage && {
      display: 'Integrations',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_INTEGRATIONS, {}),
      showWarningBadge: currentUser?.apiUserIntegrationLoginExpired ?? false,
    },

    canRenderSecurityPage && {
      display: 'Security',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_SECURITY, {}),
    },

    canAccessCategoriesConfigurationPage && {
      display: 'Categories',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_CATEGORIES, {}),
    },

    canAccessBrandingPage && {
      display: 'Branding',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_BRANDING, {}),
    },

    canAccessSubBrandsPage && {
      display: 'Sub-brands',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_SUB_BRANDS, {}),
    },

    canAccessLegalDisclaimersPage && {
      display: 'Disclaimers',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_LEGAL_DISCLAIMERS, {}),
    },

    canAccessSummaryPromptsPage && {
      display: 'AI prompts',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_SUMMARY_PROMPTS, {}),
    },

    canAccessPresentationTemplatesPage && {
      display: 'Templates',
      to: getCompletePathFromRouteKey(
        ROUTE_KEYS.ADMIN_PRESENTATION_TEMPLATES,
        {}
      ),
    },

    canAccessEducationPage && {
      display: 'Education',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_EDUCATION, {}),
    },

    canAccessDocumentSummariesReportPage && {
      display: 'Document summaries',
      to: getCompletePathFromRouteKey(
        ROUTE_KEYS.ADMIN_DOCUMENT_SUMMARIES_REPORT,
        {}
      ),
    },

    canAccessInternalTools && {
      display: 'Organizations',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_ORGANIZATIONS, {}),
    },

    canAccessInternalTools && {
      display: 'Tenants',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_TENANTS, {}),
    },

    canAccessInternalTools && {
      display: 'Configurations',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_CONFIGURATIONS, {}),
    },

    canAccessInternalTools && {
      display: 'Feature flags',
      to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_FEATURE_FLAGS, {}),
    },
  ]);
}
